'use client';

import { z as zod } from 'zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { zodResolver } from '@hookform/resolvers/zod';

import { alpha } from '@mui/material/styles';
import {
  Box,
  Stack,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { extractErrorMessage } from 'src/utils/error';

import { sendContactUsEmail } from 'src/lib/sendgrid';

import { Iconify } from 'src/components/iconify';
import { toast } from 'src/components/snackbar';

export function ContactForm() {
  const t = useTranslations('Contact.form');
  const [submitted, setSubmitted] = useState(false);

  const SUBJECT_OPTIONS = [
    { value: 'general', label: t('subjects.general') },
    { value: 'demo', label: t('subjects.demo') },
    { value: 'support', label: t('subjects.support') },
    { value: 'partnership', label: t('subjects.partnership') },
    { value: 'other', label: t('subjects.other') },
  ];

  const schema = zod.object({
    name: zod.string().min(1, { message: t('nameRequired') }),
    email: zod
      .string()
      .min(1, { message: t('emailRequired') })
      .email({ message: t('emailInvalid') }),
    subject: zod.string().optional().default(''),
    message: zod.string().min(1, { message: t('messageRequired') }),
  });

  type FormValues = zod.infer<typeof schema>;

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', subject: '', message: '' },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await sendContactUsEmail({
        from: data.email,
        text: data.message,
        name: data.name,
        subject: data.subject,
      });
      reset();
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      toast.error(extractErrorMessage(error));
    }
  };

  if (submitted) {
    return (
      <Stack
        alignItems="center"
        spacing={2}
        sx={(theme) => ({
          p: { xs: 5, md: 8 },
          textAlign: 'center',
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
          border: '1px solid',
          borderColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.06)
              : 'grey.200',
          borderRadius: 2.5,
        })}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: alpha('#22C55E', 0.1),
            border: `1px solid ${alpha('#22C55E', 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify icon="eva:checkmark-fill" width={28} sx={{ color: '#22C55E' }} />
        </Box>
        <Typography
          sx={{
            fontFamily: 'Barlow, sans-serif',
            fontWeight: 700,
            fontSize: 24,
            color: 'text.primary',
          }}
        >
          {t('successTitle')}
        </Typography>
        <Typography
          sx={{ fontSize: 15, color: 'text.secondary', maxWidth: 340, lineHeight: 1.6 }}
        >
          {t('successDescription')}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Typography
        component="h2"
        sx={{
          fontFamily: 'Barlow, sans-serif',
          fontWeight: 800,
          fontSize: { xs: 24, md: 32 },
          letterSpacing: '-0.8px',
          color: 'text.primary',
          mb: 1,
        }}
      >
        {t('title')}
      </Typography>
      <Typography
        sx={{ fontSize: { xs: 14, md: 15 }, color: 'text.secondary', mb: { xs: 4, md: 5 }, lineHeight: 1.6 }}
      >
        {t('subtitle')}
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 2.5,
          }}
        >
          <TextField
            fullWidth
            label={t('name')}
            placeholder="Anna Andersson"
            autoComplete="name"
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name')}
          />
          <TextField
            fullWidth
            label={t('email')}
            placeholder="anna@exempel.se"
            autoComplete="email"
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register('email')}
          />
          <TextField
            select
            fullWidth
            defaultValue=""
            label={t('subject')}
            sx={{ gridColumn: { sm: '1 / -1' } }}
            {...register('subject')}
          >
            <MenuItem value="" disabled>
              {t('subjectPlaceholder')}
            </MenuItem>
            {SUBJECT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={5}
            label={t('message')}
            placeholder={t('messagePlaceholder')}
            sx={{ gridColumn: { sm: '1 / -1' } }}
            error={!!errors.message}
            helperText={errors.message?.message}
            {...register('message')}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          color="secondary"
          size="large"
          loading={isSubmitting}
          endIcon={<Iconify icon="solar:plain-bold" />}
          sx={{
            mt: 3,
            px: 4.5,
            py: 1.75,
            borderRadius: 1.25,
            fontSize: 15,
            fontWeight: 600,
            width: { xs: '100%', sm: 'auto' },
            justifyContent: 'center',
            boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
            '&:hover': {
              boxShadow: (theme) => `0 8px 28px ${alpha(theme.palette.secondary.main, 0.4)}`,
            },
          }}
        >
          {t('send')}
        </Button>
      </Box>
    </Box>
  );
}
